import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Per-post job bookkeeping shared by the SMM generation hooks
 * (static post / carousel / infographic / video).
 *
 * Each post keeps its own job state and its own poller, so generating media
 * for one post never blocks, cancels, or overwrites another post's job — the
 * user can start post B while post A is still generating.
 */
export function useGenerationJobs<S extends { status: string }>() {
  const [jobs, setJobs] = useState<Record<string, S>>({});
  const stopRefs = useRef<Record<string, () => void>>({});

  const setJob = useCallback((postId: string, state: S) => {
    setJobs((prev) => ({ ...prev, [postId]: state }));
  }, []);

  // Stops polling this post's job (its callbacks will no longer fire).
  const stopPolling = useCallback((postId: string) => {
    stopRefs.current[postId]?.();
    delete stopRefs.current[postId];
  }, []);

  const trackPolling = useCallback((postId: string, stop: () => void) => {
    stopRefs.current[postId] = stop;
  }, []);

  // Forgets a finished (done/error/timeout) job so reopening the post starts
  // from a clean slate. A still-pending job is left running.
  const clear = useCallback((postId: string) => {
    setJobs((prev) => {
      if (!prev[postId] || prev[postId].status === 'pending') return prev;
      const next = { ...prev };
      delete next[postId];
      return next;
    });
  }, []);

  // Stop every poller on unmount to avoid dangling timers and
  // setState-after-unmount.
  useEffect(
    () => () => {
      Object.values(stopRefs.current).forEach((stop) => stop());
      stopRefs.current = {};
    },
    []
  );

  return { jobs, setJob, stopPolling, trackPolling, clear };
}
